import React, { useState, useEffect } from "react";
import {
    Modal,
    Form,
    Input,
    Button,
    Typography,
    notification,
    Alert,
    Steps,
    Row,
    Col
} from "antd";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import SubtenantApi from "../common/Apis/SubtenantApi";
import { createDefaultSubTenantAdminUser } from "../../models/SubTenant/SubTenantAdminUserDTO";
import UserApi from "../common/Apis/UserApi";
import { createDefaultSubTenant } from "../../models/SubTenant/SubTenantDTO";
import RatesApi from "../common/Apis/RatesApi";
import { createDefaultSubTenantRate } from "../../models/Rate/SubTenantRateDTO";

const { Title } = Typography;
const { Step } = Steps;

const CreateSubTenantModal = ({ visible, onCancel, tenantId }) => {
    const [form] = Form.useForm();
    const [currentStep, setCurrentStep] = useState(0);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [tenantRates, setTenantRates] = useState(null);
    const [loadingTenantRates, setLoadingTenantRates] = useState(false);
    
    // Store form data for all steps
    const [formData, setFormData] = useState({
        subTenantCode: '',
        subTenantName: '',
        rate: '',
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    
    // Store API results
    const [apiResults, setApiResults] = useState({
        subTenantId: null,
        subTenantRateId: null,
        rateAdded: false,
        adminCreated: false,
        isSubtenantCreated: false,
        hasAttemptedSave: false
    });
    
    // Track which step has validation errors
    const [stepWithError, setStepWithError] = useState(null);

    const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));

    // Reset all states function
    const resetAllStates = () => {
        setIsButtonDisabled(true);
        setCurrentStep(0);
        setApiError(null);
        setStepWithError(null);
        setApiResults({
            subTenantId: null,
            subTenantRateId: null,
            rateAdded: false,
            adminCreated: false,
            isSubtenantCreated: false,
            hasAttemptedSave: false
        });
        setFormData({
            subTenantCode: '',
            subTenantName: '',
            rate: '',
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            confirmPassword: ''
        });
        form.resetFields();
        setTenantRates(null);
    };

    useEffect(() => {
        if (visible && tenantId) {
            // Reset all states when modal opens
            resetAllStates();
            fetchTenantRates();
        }
    }, [visible, tenantId, form]);

    // Handle modal close
    const handleCancel = () => {
        resetAllStates();
        onCancel();
    };

    const fetchTenantRates = () => {
        if (!tenantId) {
            console.error("No tenantId provided");
            return;
        }

        setLoadingTenantRates(true);
        RatesApi.GetActiveTenantRate(tenantId)
            .then((response) => {
                if (response?.payload) {
                    setTenantRates(response.payload);
                    console.log("Tenant rates loaded:", response.payload);
                } else {
                    console.warn("No payload in tenant rates response");
                }
            })
            .catch((error) => {
                console.error("Failed to fetch tenant rates:", error);
            })
            .finally(() => {
                setLoadingTenantRates(false);
            });
    };

    const handleFieldsChange = (changedFields, allFields) => {
        // Only update formData if fields are not disabled
        const currentValues = form.getFieldsValue();
        setFormData(prev => ({
            ...prev,
            ...currentValues
        }));

        // Determine which fields to validate based on current step
        let fields = [];
        switch (currentStep) {
            case 0:
                fields = ["subTenantCode", "subTenantName"];
                break;
            case 1:
                fields = ["rate"];
                break;
            case 2:
                fields = ["firstName", "lastName", "email", "password", "confirmPassword"];
                break;
            default:
                fields = [];
        }

        // Check if the current step's fields are disabled
        const isStepDisabled = isFieldDisabled(currentStep);
        
        // If fields are disabled, enable the button
        if (isStepDisabled) {
            setIsButtonDisabled(false);
            return;
        }

        // For Step 3, we always want the button enabled if user has attempted save
        if (currentStep === 2 && apiResults.hasAttemptedSave) {
            // Check if admin fields are filled
            const adminFields = ["firstName", "lastName", "email", "password", "confirmPassword"];
            const adminValues = form.getFieldsValue(adminFields);
            const allAdminFieldsFilled = adminFields.every(field => {
                const value = adminValues[field];
                return value && (typeof value === 'string' ? value.trim() !== '' : true);
            });
            
            if (allAdminFieldsFilled) {
                const fieldErrors = form.getFieldsError(adminFields);
                const hasErrors = fieldErrors.some(f => f.errors.length > 0);
                if (!hasErrors) {
                    setIsButtonDisabled(false);
                    return;
                }
            }
        }

        // Check if any of the fields have been touched
        const touched = form.isFieldsTouched(fields, true);
        
        // Check if any of the fields have errors
        const fieldErrors = form.getFieldsError(fields);
        const hasErrors = fieldErrors.some(f => f.errors.length > 0);
        
        // Get all field values to check if they are empty
        const fieldValues = form.getFieldsValue(fields);
        const hasEmptyFields = fields.some(field => {
            const value = fieldValues[field];
            return !value || (typeof value === 'string' && value.trim() === '');
        });

        // Enable button only if all fields are filled, have no errors, and are touched
        const allFieldsFilled = !hasEmptyFields;
        const isValid = allFieldsFilled && !hasErrors && touched;
        
        setIsButtonDisabled(!isValid);
    };

    const validateStep = (step) => {
        let fields = [];
        switch (step) {
            case 0:
                fields = ["subTenantCode", "subTenantName"];
                break;
            case 1:
                fields = ["rate"];
                break;
            case 2:
                fields = ["firstName", "lastName", "email", "password", "confirmPassword"];
                break;
            default:
                return Promise.resolve(true);
        }

        // If fields are disabled, skip validation
        if (isFieldDisabled(step)) {
            return Promise.resolve(true);
        }

        return new Promise((resolve, reject) => {
            form.validateFields(fields)
                .then(values => {
                    // Update form data with validated values
                    setFormData(prev => ({
                        ...prev,
                        ...values
                    }));
                    resolve(values);
                })
                .catch(error => {
                    reject(error);
                    notification.error({ 
                        message: "Please fix form errors first.",
                        description: error.errorFields?.map(f => f.errors.join(', ')).join(', ')
                    });
                });
        });
    };

    const handleNext = async () => {
        try {
            setApiError(null);
            
            if (currentStep < 2) {
                // Just validate and move to next step without saving
                await validateStep(currentStep);
                setCurrentStep(currentStep + 1);
                // Reset button state for the new step
                setIsButtonDisabled(true);
                // Trigger validation for the new step after a short delay
                setTimeout(() => {
                    handleFieldsChange();
                }, 100);
            } else {
                // Step 2: Execute all API calls sequentially
                await executeAllApiCalls();
            }
        } catch (error) {
            console.error("Validation error:", error);
            // Stay on current step if validation fails
        }
    };

    // Function to update subtenant (only used when subtenant was already created successfully)
    const updateSubtenant = async (subTenantId, data) => {
        if (!apiResults.isSubtenantCreated) {
            console.log("Subtenant was not previously created, skipping update");
            return;
        }

        const updateData = JSON.stringify({
            tenantId: tenantId,
            subTenantCode: data.subTenantCode,
            subTenantName: data.subTenantName,
            tenantDTO: {
                id: tenantId,
                tenantApiKey: decodedToken?.tenantApiKey,
            },
        });

        try {
            await SubtenantApi.PutSubtenant(subTenantId, updateData);
            console.log("Subtenant updated successfully");
        } catch (error) {
            console.error("Failed to update subtenant:", error);
            throw error;
        }
    };

    // Function to update rate (only used when rate was already created successfully)
    const updateRate = async (rateId, data) => {
        if (!apiResults.rateAdded) {
            console.log("Rate was not previously created, skipping update");
            return;
        }

        const updateData = {
            subTenantRate: parseFloat(data.rate),
            tenantDTO: { id: tenantId }
        };

        try {
            await RatesApi.UpdateTenantRate(rateId, updateData);
            console.log("Rate updated successfully");
        } catch (error) {
            console.error("Failed to update rate:", error);
            throw error;
        }
    };

    const executeAllApiCalls = async () => {
        setLoading(true);
        setApiError(null);
        let newSubTenantId = null;
        let subTenantRateId = null;
        
        try {
            // Mark that user has attempted to save
            setApiResults(prev => ({ ...prev, hasAttemptedSave: true }));
            
            // Validate all steps first
            await validateStep(0);
            await validateStep(1);
            await validateStep(2);

            // Step 1: Create SubTenant (only if not already created)
            if (!apiResults.isSubtenantCreated) {
                const subTenantPayload = createDefaultSubTenant({
                    subTenantCode: formData.subTenantCode,
                    subTenantName: formData.subTenantName,
                    tenantDTO: { id: tenantId }
                });

                try {
                    const subTenantResponse = await SubtenantApi.Createsubtenants(subTenantPayload);
                    
                    if (!subTenantResponse?.payload?.id) {
                        throw new Error("Failed to create store/shop. No ID returned.");
                    }
                    
                    newSubTenantId = subTenantResponse.payload.id;
                    setApiResults(prev => ({ 
                        ...prev, 
                        subTenantId: newSubTenantId,
                        isSubtenantCreated: true
                    }));
                } catch (error) {
                    // Handle SubTenant creation error specifically
                    const errorMessage = error.apiError?.message || error.message || "Failed to create store/shop.";
                    
                    // Set error to step 0 (Store Info)
                    setStepWithError(0);
                    setCurrentStep(0);
                    setApiError(`Failed at Store Info: ${errorMessage}`);
                    
                    notification.error({
                        message: "Error Creating Store/Shop",
                        description: errorMessage,
                        duration: 5
                    });
                    
                    setLoading(false);
                    return; // Stop execution
                }
            } else {
                // Subtenant already created, use existing ID
                newSubTenantId = apiResults.subTenantId;
                console.log("Using existing subtenant ID:", newSubTenantId);
            }

            // Step 2: Add Rate for SubTenant (only if not already added)
            if (!apiResults.rateAdded) {
                try {
                    const subTenantList = [{
                        id: newSubTenantId,
                        subTenantRate: parseFloat(formData.rate)
                    }];

                    const ratePayload = createDefaultSubTenantRate({
                        tenantDTO: { id: tenantId },
                        subTenantList: subTenantList,
                        agreedRate: null,
                        billingCycle: tenantRates?.billingCycle || "MONTHLY",
                        currency: tenantRates?.currency || tenantRates?.tenantDTO?.currency || "USD",
                        tenantRate: parseFloat(tenantRates?.tenantRate || tenantRates?.tenantDTO?.tenantRate || 0),
                    });

                    const rateResponse = await RatesApi.AddRateForTenant(ratePayload);
                    
                    // Get the rate ID from the response
                    if (rateResponse?.payload?.id) {
                        subTenantRateId = rateResponse.payload.id;
                    } else if (rateResponse?.payload?.subTenantList && rateResponse.payload.subTenantList.length > 0) {
                        subTenantRateId = rateResponse.payload.subTenantList[0]?.id;
                    }
                    
                    setApiResults(prev => ({ 
                        ...prev, 
                        rateAdded: true,
                        subTenantRateId: subTenantRateId
                    }));
                } catch (error) {
                    // If Rate creation fails, update the subtenant if it was previously created
                    const errorMessage = error.apiError?.message || error.message || "Failed to save rate information.";
                    
                    // Only update subtenant if it was created in this session
                    if (apiResults.isSubtenantCreated && newSubTenantId) {
                        try {
                            // Update the subtenant with the latest data
                            const updateData = {
                                subTenantCode: formData.subTenantCode,
                                subTenantName: formData.subTenantName
                            };
                            
                            await updateSubtenant(newSubTenantId, updateData);
                            
                            notification.warning({
                                message: "Rate Creation Failed",
                                description: "Rate could not be created. Subtenant was updated with the new data.",
                                duration: 5
                            });
                        } catch (updateError) {
                            console.error("Failed to update subtenant:", updateError);
                        }
                    }
                    
                    // Set error to step 1 (Rate Info)
                    setStepWithError(1);
                    setCurrentStep(1);
                    setApiError(`Failed at Rate Info: ${errorMessage}`);
                    
                    notification.error({
                        message: "Error Saving Rate",
                        description: errorMessage,
                        duration: 5
                    });
                    
                    setLoading(false);
                    return; // Stop execution
                }
            } else {
                // Rate already added, use existing ID
                subTenantRateId = apiResults.subTenantRateId;
                console.log("Using existing rate ID:", subTenantRateId);
            }

            // Step 3: Create Admin User (only if not already created)
            if (!apiResults.adminCreated) {
                try {
                    const adminPayload = createDefaultSubTenantAdminUser({
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        email: formData.email,
                        password: formData.password,
                        matchingPassword: formData.confirmPassword,
                        tenantDTO: { id: tenantId },
                        subTenantDTO: { id: newSubTenantId },
                    });

                    await UserApi.CreateTenantAdmin(adminPayload);
                    setApiResults(prev => ({ ...prev, adminCreated: true }));
                } catch (error) {
                    // If Admin creation fails, update both subtenant and rate if they were created in this session
                    const errorMessage = error.apiError?.message || error.message || "Failed to create admin user.";
                    
                    // Only update if subtenant and rate were created in this session
                    if (apiResults.isSubtenantCreated && apiResults.rateAdded) {
                        try {
                            // Update the subtenant with the latest data
                            const subtenantUpdateData = {
                                subTenantCode: formData.subTenantCode,
                                subTenantName: formData.subTenantName
                            };
                            await updateSubtenant(newSubTenantId, subtenantUpdateData);
                            
                            // Update the rate with the latest data
                            if (subTenantRateId) {
                                await updateRate(subTenantRateId, { rate: formData.rate });
                            }
                            
                            notification.warning({
                                message: "Admin Creation Failed",
                                description: "Admin user could not be created. Subtenant and rate were updated with the new data.",
                                duration: 5
                            });
                        } catch (updateError) {
                            console.error("Failed to update subtenant and rate:", updateError);
                        }
                    }
                    
                    // Set error to step 2 (Admin Info)
                    setStepWithError(2);
                    setCurrentStep(2);
                    setApiError(`Failed at Admin Info: ${errorMessage}`);
                    
                    notification.error({
                        message: "Error Creating Admin User",
                        description: errorMessage,
                        duration: 5
                    });
                    
                    setLoading(false);
                    return; // Stop execution
                }
            } else {
                console.log("Admin already created, skipping");
            }

            // All steps completed successfully
            notification.open({
                message: <span style={{ fontWeight: "bold" }}>New Store / Shop added successfully!</span>,
                closeIcon: false,
                duration: 3,
                style: {
                    backgroundColor: "#b3ffb3",
                    color: "green",
                },
            });
            handleCancel();

        } catch (error) {
            console.error("Unexpected error during API calls:", error);
            const errorMessage = error.message || "An unexpected error occurred.";
            
            setApiError(errorMessage);
            notification.error({
                message: "Unexpected Error",
                description: errorMessage,
                duration: 5
            });
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            setApiError(null);
            setStepWithError(null);
            
            // Re-validate the previous step's fields
            setTimeout(() => {
                let fields = [];
                switch (currentStep - 1) {
                    case 0:
                        fields = ["subTenantCode", "subTenantName"];
                        break;
                    case 1:
                        fields = ["rate"];
                        break;
                    case 2:
                        fields = ["firstName", "lastName", "email", "password", "confirmPassword"];
                        break;
                    default:
                        fields = [];
                }
                
                // Mark fields as touched to enable the button
                fields.forEach(field => {
                    form.setFields([{
                        name: field,
                        touched: true
                    }]);
                });
                
                // Update button state based on current values
                handleFieldsChange();
            }, 100);
        }
    };

    // Check if a field should be disabled
    const isFieldDisabled = (step) => {
        // If user has attempted save and the step's data was already created/saved
        if (apiResults.hasAttemptedSave) {
            if (step === 0 && apiResults.isSubtenantCreated) {
                return true; // Subtenant already created, disable step 0 fields
            }
            if (step === 1 && apiResults.rateAdded) {
                return true; // Rate already added, disable step 1 fields
            }
            // Step 2 (Admin) fields should never be disabled because admin creation might fail
            if (step === 2) {
                return false;
            }
        }
        return false;
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                const isStep0Disabled = isFieldDisabled(0);
                return (
                    <>
                        {apiError && (
                            <Alert 
                                message={apiError} 
                                type="error" 
                                showIcon 
                                className="mb-3" 
                                closable
                                onClose={() => setApiError(null)}
                            />
                        )}
                        
                        {tenantRates && (
                            <Alert 
                                message={`Default rate for this tenant: ${tenantRates.currency || tenantRates?.tenantDTO?.currency || '$'}${tenantRates.agreedRate || 'Not set'}`} 
                                type="info" 
                                showIcon 
                                className="mb-3" 
                            />
                        )}

                        {isStep0Disabled && (
                            <Alert 
                                message="Store/Shop already created. You can view but cannot edit these fields." 
                                type="warning" 
                                showIcon 
                                className="mb-3" 
                            />
                        )}
                        
                        <Form
                            form={form}
                            layout="vertical"
                            name="sub_tenant_form"
                            onFieldsChange={handleFieldsChange}
                            initialValues={formData}
                        >
                            <Form.Item
                                label={
                                    <span>
                                        Store / Shop Code <span style={{ color: "red" }}>*</span>
                                    </span>
                                }
                                name="subTenantCode"
                                rules={[
                                    { required: true, message: "Please enter Store / Shop Code" },
                                    {
                                        pattern: /^[A-Za-z0-9]+$/,
                                        message: "Only letters and numbers allowed. No spaces or special chars."
                                    }
                                ]}
                            >
                                <Input 
                                    placeholder="Enter Store / Shop Code" 
                                    disabled={isStep0Disabled}
                                />
                            </Form.Item>
                            <Form.Item
                                label={
                                    <span>
                                        Store / Shop Name <span style={{ color: "red" }}>*</span>
                                    </span>
                                }
                                name="subTenantName"
                                rules={[
                                    { required: true, message: "Please enter Store / Shop Name" }
                                ]}
                            >
                                <Input 
                                    placeholder="Enter Store / Shop Name" 
                                    disabled={isStep0Disabled}
                                />
                            </Form.Item>
                        </Form>
                    </>
                );
            case 1:
                const isStep1Disabled = isFieldDisabled(1);
                return (
                    <>
                        {apiError && (
                            <Alert 
                                message={apiError} 
                                type="error" 
                                showIcon 
                                className="mb-3"
                                closable
                                onClose={() => setApiError(null)}
                            />
                        )}

                        {isStep1Disabled && (
                            <Alert 
                                message="Rate already added. You can view but cannot edit this field." 
                                type="warning" 
                                showIcon 
                                className="mb-3" 
                            />
                        )}
                        
                        <Form
                            form={form}
                            layout="vertical"
                            name="rate_form"
                            onFieldsChange={handleFieldsChange}
                            initialValues={formData}
                        >
                            <Form.Item
                                name="rate"
                                label={
                                    <span>
                                        Rate <span style={{ color: "red" }}>*</span>
                                    </span>
                                }
                                rules={[
                                    {
                                        validator: (_, value) => {
                                            if (!value) {
                                                return Promise.reject(new Error("Please enter rate"));
                                            }
                                            const floatRegex = /^[+-]?\d+(\.\d+)?$/;
                                            if (floatRegex.test(value)) {
                                                if (parseFloat(value) < 0) {
                                                    return Promise.reject(new Error("Rate cannot be negative"));
                                                }
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error("Please enter a valid number (e.g., 10.5, 100, 0.75)"));
                                        },
                                    }
                                ]}
                            >
                                <Input 
                                    placeholder="Enter rate (e.g., 10.5, 100, 0.75)" 
                                    addonBefore={tenantRates?.currency || tenantRates?.tenantDTO?.currency || '$'}
                                    type="number"
                                    step="0.01"
                                    disabled={isStep1Disabled}
                                />
                            </Form.Item>
                            <div className="text-muted small mt-2">
                                {tenantRates?.agreedRate ? (
                                    <span>Default tenant rate: <strong>{tenantRates.currency || tenantRates?.tenantDTO?.currency || '$'}{tenantRates.agreedRate}</strong>. You can override this for the specific store/shop.</span>
                                ) : (
                                    "Note: This rate will be used for billing calculations."
                                )}
                            </div>
                        </Form>
                    </>
                );
            case 2:
                return (
                    <>
                        {apiError && (
                            <Alert 
                                message={apiError} 
                                type="error" 
                                showIcon 
                                className="mb-3"
                                closable
                                onClose={() => setApiError(null)}
                            />
                        )}
                        <Form
                            onFieldsChange={handleFieldsChange}
                            form={form}
                            name="admin-form"
                            layout="vertical"
                            initialValues={formData}
                        >
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="firstName"
                                        label={
                                            <span>
                                                First Name <span style={{ color: "red" }}>*</span>
                                            </span>
                                        }
                                        rules={[{ required: true, message: "Please enter first name" }]}
                                        labelCol={{ span: 24 }}
                                        wrapperCol={{ span: 24 }}
                                    >
                                        <Input placeholder="First Name" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="lastName"
                                        label={
                                            <span>
                                                Last Name <span style={{ color: "red" }}>*</span>
                                            </span>
                                        }
                                        rules={[{ required: true, message: "Please enter last name" }]}
                                        labelCol={{ span: 24 }}
                                        wrapperCol={{ span: 24 }}
                                    >
                                        <Input placeholder="Last Name" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row>
                                <Col span={24}>
                                    <Form.Item
                                        name="email"
                                        label={
                                            <span>
                                                Email Address <span style={{ color: "red" }}>*</span>
                                            </span>
                                        }
                                        rules={[
                                            { required: true, message: "Please enter email address" },
                                            { type: "email", message: "Please enter a valid email address" }
                                        ]}
                                        labelCol={{ span: 24 }}
                                        wrapperCol={{ span: 24 }}
                                    >
                                        <Input placeholder="Email address" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="password"
                                        label={
                                            <span>
                                                Password <span style={{ color: "red" }}>*</span>
                                            </span>
                                        }
                                        rules={[{ required: true, message: "Please enter password" }]}
                                        validateTrigger="onChange"
                                        labelCol={{ span: 24 }}
                                        wrapperCol={{ span: 24 }}
                                    >
                                        <Input.Password
                                            placeholder="Password"
                                            iconRender={(visible) =>
                                                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                                            }
                                        />
                                    </Form.Item>
                                </Col>

                                <Col span={12}>
                                    <Form.Item
                                        name="confirmPassword"
                                        label={
                                            <span>
                                                Re-enter Password <span style={{ color: "red" }}>*</span>
                                            </span>
                                        }
                                        dependencies={["password"]}
                                        rules={[
                                            { required: true, message: "Please re-enter password" },
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    if (!value || getFieldValue("password") === value) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error("Passwords do not match"));
                                                },
                                            }),
                                        ]}
                                        validateTrigger="onChange"
                                        labelCol={{ span: 24 }}
                                        wrapperCol={{ span: 24 }}
                                    >
                                        <Input.Password
                                            placeholder="Re-enter Password"
                                            iconRender={(visible) =>
                                                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                                            }
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <Modal
            open={visible}
            width={600}
            onCancel={handleCancel}
            footer={null}
            maskClosable={false}
        >
            <Title level={4} className="text-center mb-3">
                Add new store / shop
            </Title>
            
            {loadingTenantRates && (
                <Alert 
                    message="Loading tenant rate information..." 
                    type="info" 
                    showIcon 
                    className="mb-3" 
                />
            )}
            
            <Steps current={currentStep} size="small" className="mb-4 px-4">
                <Step title="Store Info" />
                <Step title="Rate Info" />
                <Step title="Admin Info" />
            </Steps>
            
            {renderStepContent()}
            
            <div className="d-flex justify-content-between mt-4">
                {currentStep > 0 && (
                    <Button
                        onClick={handleBack}
                        disabled={loading || loadingTenantRates}
                        style={{ minWidth: "48%" }}
                    >
                        Back
                    </Button>
                )}
                <Button
                    type="primary"
                    onClick={handleNext}
                    disabled={isButtonDisabled || loading || loadingTenantRates}
                    style={{ 
                        width: currentStep === 0 ? "100%" : "calc(100% - 110px)",
                        marginLeft: currentStep > 0 ? "10px" : "0"
                    }}
                    loading={loading}
                >
                    {currentStep === 0 ? "Next" : 
                     currentStep === 1 ? "Next" : 
                     "Complete & Close"}
                </Button>
            </div>
        </Modal>
    );
};

export default CreateSubTenantModal;